import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner, Tab, Tabs } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaSave, FaSpinner, FaDollarSign, FaCog, FaInfoCircle, FaEnvelope, FaBell, FaHandHoldingUsd, FaCoins, FaHeart } from 'react-icons/fa';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import './Settings.css';
import { useAuthContext } from '../../context/AuthContext';
import { settingsApi } from '../../api/settings';
import locationApi from '../../api/locations/locationApi';
import AsyncSelect from 'react-select/async';

const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  const datePart = String(dateStr).split('T')[0].split(' ')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return datePart;
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const EventSettings = () => {
  const { user, events, isAuthenticated } = useAuthContext();
  const currentEvent = events?.[0];

  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // General Settings State
  const [generalSettings, setGeneralSettings] = useState({
    id: null,
    event_title: '',
    event_type: '',
    event_description: '',
    event_date: '',
    event_location: '',
    location_id: null,
    mobile: '',
    email: '',
    ibada_location: '',
    ibada_time: '',
    reception_location: '',
    reception_time: '',
    event_message: ''
  });

  // Price Settings State
  const [priceSettings, setPriceSettings] = useState({
    single_minimum_rate: '0',
    double_minimum_rate: '0',
    default_pledge_amount: '0'
  });

  // Message Templates State (all message templates here)
  const [messageTemplates, setMessageTemplates] = useState({
    invitation_intro: '',
    invitation_info: '',
    event_information_message: '',
    pledge_reminder_message: '',
    pledge_acknowledgement_message: '',
    cash_acknowledgement_message: '',
    event_invitation_message: '',
    event_thanknote_message: ''
  });

  // Notification Settings State (only enable/disable switches)
  const [notificationSettings, setNotificationSettings] = useState({
    auto_send_pledges: true,
    auto_send_funds: true
  });

  // Fetch settings on component mount
  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      fetchEventSettings();
      fetchCustomTemplates();
    } else if (!isAuthenticated) {
      setError('You must be logged in to view settings');
    }
  }, [isAuthenticated, currentEvent]);

  const fetchEventSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsApi.getEventSettings();
      const data = response?.data?.data || response?.data || response || {};

      setGeneralSettings({
        id: data.id || currentEvent?.id,
        event_title: data.event_title || currentEvent?.event_title || '',
        event_type: data.event_type || currentEvent?.event_type || '',
        event_description: data.event_description || currentEvent?.event_description || '',
        event_date: formatDateForInput(data.event_date || currentEvent?.event_date),
        event_location: typeof data.event_location === 'object' ? (data.event_location?.display_name || data.event_location?.name || '') : (data.event_location || ''),
        location_id: data.location_id || null,
        mobile: data.mobile || currentEvent?.mobile || '',
        email: data.email || currentEvent?.email || '',
        ibada_location: typeof data.event_location_ibada === 'object' ? (data.event_location_ibada?.display_name || data.event_location_ibada?.name || '') : (data.event_location_ibada || data.ibada_location || ''),
        ibada_time: data.event_time_ibada || data.ibada_time || '',
        reception_location: typeof data.event_location_reception === 'object' ? (data.event_location_reception?.display_name || data.event_location_reception?.name || '') : (data.event_location_reception || data.reception_location || ''),
        reception_time: data.event_time_reception || data.reception_time || '',
        event_message: data.event_message || currentEvent?.event_message || ''
      });

      // If we have a location_id but event_location might just be a slug, fetch the display name
      if (data.location_id) {
        locationApi.getLocationById(data.location_id).then(locData => {
          if (locData && (locData.display_name || locData.name)) {
            setGeneralSettings(prev => ({
              ...prev,
              event_location: locData.display_name || locData.name
            }));
          }
        });
      }

      // Extract price settings from response
      if (data.single_minimum_rate !== undefined) {
        setPriceSettings({
          single_minimum_rate: data.single_minimum_rate?.toString() || '0',
          double_minimum_rate: data.double_minimum_rate?.toString() || '0',
          default_pledge_amount: data.default_pledge_amount?.toString() || '0'
        });
      }

      // Extract auto send settings
      if (data.auto_send_pledges !== undefined) {
        setNotificationSettings({
          auto_send_pledges: data.auto_send_pledges === 1,
          auto_send_funds: data.auto_send_funds === 1
        });
      }
    } catch (err) {
      console.error('Error fetching event settings:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please login again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view these settings.');
      } else {
        setError('Failed to load event settings. Using default values.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomTemplates = async () => {
    try {
      const response = await settingsApi.getCustomTemplates();
      const data = response?.data?.data || response?.data || response || {};

      setMessageTemplates({
        invitation_intro: data.invitation_intro || "Familia pamoja na kamati ya maadalizi, wanayo furaha kukualika/kuwaalika",
        invitation_info: data.invitation_info || "kwenye #shughuli  ya #title Itakayofanyika #mahali ,#tarehe kuanzia #muda. Karibu tushiriki pamoja baraka hizi",
        event_information_message: data.event_information_message || '',
        pledge_reminder_message: data.pledge_reminder_message || '',
        pledge_acknowledgement_message: data.pledge_aknowledgement_message || data.pledge_acknowledgement_message || '',
        cash_acknowledgement_message: data.cash_aknowledgement_message || data.cash_acknowledgement_message || '',
        event_invitation_message: data.event_invitation_message || '',
        event_thanknote_message: data.event_thanknote_message || ''
      });
    } catch (err) {
      console.error('Error fetching custom templates:', err);
      // Set default values
      setMessageTemplates({
        invitation_intro: "Familia pamoja na kamati ya maadalizi, wanayo furaha kukualika/kuwaalika",
        invitation_info: "kwenye #shughuli  ya #title Itakayofanyika #mahali ,#tarehe kuanzia #muda. Karibu tushiriki pamoja baraka hizi",
        event_information_message: 'Habari #jina, Tuna furaha kukuarifu #shughuli ya #title itakayofanyika #tarehe (siku #siku kutoka sasa). Mawasiliano #simu.',
        pledge_reminder_message: 'Habari #jina, kwa upendo mkubwa tunakukumbusha kumaliza/kupunguza ahadi yako ya #title.',
        pledge_acknowledgement_message: 'Asante #jina kwa ahadi yako ya mchango wa #title.',
        cash_acknowledgement_message: 'Ahsante sana #jina, Mchango wako umepokelewa kwa furaha kubwa!',
        event_invitation_message: 'Karibu #jina, kwenye #shughuli ya #title. Namba ya kadi #kadi. #tarehe ,#mahali , #muda.',
        event_thanknote_message: 'Asante kwa kushiriki nasi katika sherehe yetu.'
      });
    }
  };

  const handleGeneralChange = (e) => {
    const { name, value } = e.target;
    setGeneralSettings(prev => ({ ...prev, [name]: value }));
  };

  const loadLocationOptions = async (inputValue) => {
    if (inputValue.length < 3) {
      return [];
    }
    const results = await locationApi.searchLocations(inputValue);
    return results.map(loc => ({
      value: loc.display_name || loc.name || loc.title || loc.id || loc,
      label: loc.display_name || loc.name || loc.title || loc,
      data: loc
    }));
  };

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    setPriceSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleMessageTemplateChange = (e) => {
    const { name, value } = e.target;
    setMessageTemplates(prev => ({ ...prev, [name]: value }));
  };

  const handleNotificationChange = async (e) => {
    const { name, checked } = e.target;
    const newSettings = { ...notificationSettings, [name]: checked };
    setNotificationSettings(newSettings);

    // Auto-save the notification settings when toggled
    setSaving(true);
    try {
      const payload = {
        auto_send_pledges: newSettings.auto_send_pledges ? 1 : 0,
        auto_send_funds: newSettings.auto_send_funds ? 1 : 0,
        notifications_subscription: (newSettings.auto_send_pledges || newSettings.auto_send_funds) ? 1 : 0
      };
      const response = await settingsApi.updateEventSettings(generalSettings.id, payload);
      handleApiResponse(response);
    } catch (err) {
      console.error('Error saving notification settings:', err);
      handleApiResponse(err);
    } finally {
      setSaving(false);
    }
  };

  // Save General Settings
  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        event_description: generalSettings.event_description,
        event_date: generalSettings.event_date,
        event_location: generalSettings.event_location,
        location_id: generalSettings.location_id,
        email: generalSettings.email,
        event_location_ibada: generalSettings.ibada_location,
        event_time_ibada: generalSettings.ibada_time,
        event_location_reception: generalSettings.reception_location,
        event_time_reception: generalSettings.reception_time,
        event_message: generalSettings.event_message
      };
      const response = await settingsApi.updateEventSettings(generalSettings.id, payload);
      handleApiResponse(response);
    } catch (err) {
      console.error('Error saving general settings:', err);
      handleApiResponse(err);
    } finally {
      setSaving(false);
    }
  };

  // Save Price Settings
  const handleSavePrice = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        single_minimum_rate: parseFloat(priceSettings.single_minimum_rate) || 0,
        double_minimum_rate: parseFloat(priceSettings.double_minimum_rate) || 0,
        default_pledge_amount: parseFloat(priceSettings.default_pledge_amount) || 0
      };
      const response = await settingsApi.updateEventSettings(generalSettings.id, payload);
      handleApiResponse(response);
    } catch (err) {
      console.error('Error saving price settings:', err);
      handleApiResponse(err);
    } finally {
      setSaving(false);
    }
  };

  // Save Message Templates (all templates in one go)
  const handleSaveMessageTemplates = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Map the form fields to the API expected field names
      const payload = {
        invitation_intro: messageTemplates.invitation_intro,
        invitation_info: messageTemplates.invitation_info,
        event_information_message: messageTemplates.event_information_message,
        pledge_reminder_message: messageTemplates.pledge_reminder_message,
        pledge_aknowledgement_message: messageTemplates.pledge_acknowledgement_message,
        cash_aknowledgement_message: messageTemplates.cash_acknowledgement_message,
        event_invitation_message: messageTemplates.event_invitation_message,
        event_thanknote_message: messageTemplates.event_thanknote_message
      };

      const response = await settingsApi.updateCustomTemplates(payload);
      handleApiResponse(response);
    } catch (err) {
      console.error('Error saving message templates:', err);
      handleApiResponse(err);
    } finally {
      setSaving(false);
    }
  };

  // Save Notification Settings (Fallback button)
  const handleSaveNotifications = async (e) => {
    e.preventDefault();
    // Notifications are now auto-saved on toggle, but this remains as fallback
    toast.info('Notifications are auto-saved when toggled.');
  };

  if (loading) {
    return (
      <Container className="settings-container p-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <FaSpinner className="fa-spin" size={48} color="#4169e1" />
            <p className="mt-3">Loading settings...</p>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="settings-container p-4">
      <div className="text-center mb-4">
        <h1 className="stylish-heading mb-1" style={{ color: '#4169e1' }}>
          Event Settings
        </h1>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Tabs id="event-settings-tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="settings-nav-tabs mb-4">

        {/* General Settings Tab */}
        <Tab eventKey="general" title={<span><FaCog className="me-2" /> General Settings</span>}>
          <Card className="settings-card">
            <Card.Body>
              <h3 className="settings-title">General Settings</h3>
              <Form onSubmit={handleSaveGeneral}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Event Title</Form.Label>
                      <Form.Control
                        type="text"
                        name="event_title"
                        value={generalSettings.event_title}
                        disabled
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">Event title cannot be changed.</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Event Type</Form.Label>
                      <Form.Control
                        type="text"
                        name="event_type"
                        value={generalSettings.event_type}
                        disabled
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">Event type cannot be changed.</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Event Location</Form.Label>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={false}
                        loadOptions={loadLocationOptions}
                        placeholder="Event location (e.g., Arusha)"
                        noOptionsMessage={({ inputValue }) =>
                          inputValue.length < 3 ? "Andika walau herufi 3 kutafuta" : "Hakuna mahali palipopatikana"
                        }
                        value={generalSettings.event_location ? { value: generalSettings.event_location, label: generalSettings.event_location } : null}
                        onChange={(selectedOption) => {
                          setGeneralSettings(prev => ({
                            ...prev,
                            event_location: selectedOption ? selectedOption.label : '',
                            location_id: selectedOption?.data?.id || null
                          }));
                        }}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? '#4169e1' : '#ced4da',
                            borderRadius: '0.375rem',
                            padding: '2px'
                          })
                        }}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={generalSettings.email}
                        onChange={handleGeneralChange}
                        placeholder="contact@example.com"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Ibada Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="ibada_time"
                        value={generalSettings.ibada_time}
                        onChange={handleGeneralChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Reception Time</Form.Label>
                      <Form.Control
                        type="time"
                        name="reception_time"
                        value={generalSettings.reception_time}
                        onChange={handleGeneralChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Event Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="event_description"
                        value={generalSettings.event_description}
                        onChange={handleGeneralChange}
                        placeholder="Event description"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Event Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="event_date"
                        value={generalSettings.event_date}
                        onChange={handleGeneralChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Mobile Number</Form.Label>
                      <Form.Control
                        type="tel"
                        name="mobile"
                        value={generalSettings.mobile}
                        disabled
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">Mobile number cannot be changed.</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Ibada Location</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ibada Location"
                        value={generalSettings.ibada_location}
                        onChange={(e) => setGeneralSettings(prev => ({ ...prev, ibada_location: e.target.value }))}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Reception Location</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Reception venue"
                        value={generalSettings.reception_location}
                        onChange={(e) => setGeneralSettings(prev => ({ ...prev, reception_location: e.target.value }))}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Event Reception Message (for the Website)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="event_message"
                        value={generalSettings.event_message}
                        onChange={handleGeneralChange}
                        placeholder="Familia pamoja na kamati ya maandalizi..."
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-center mt-4">
                  <Button type="submit" variant="primary" className="settings-btn-primary" disabled={saving}>
                    {saving ? <Spinner animation="border" size="sm" /> : <FaSave className="me-2" />}
                    {saving ? ' Saving...' : ' Save General Settings'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        {/* Price Settings Tab */}
        <Tab eventKey="price" title={<span><FaDollarSign className="me-2" /> Price Settings</span>}>
          <Card className="settings-card">
            <Card.Body>
              <h3 className="settings-title">Price Settings</h3>
              <Alert variant="info" className="mb-4">
                <FaInfoCircle className="me-2" /> These settings affect pledge minimums and default amounts.
              </Alert>
              <Form onSubmit={handleSavePrice}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Single Minimum Rate (TZS)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        name="single_minimum_rate"
                        value={priceSettings.single_minimum_rate}
                        onChange={handlePriceChange}
                        placeholder="Minimum amount for single card"
                      />
                      <Form.Text className="text-muted">Minimum pledge amount for a single card.</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Default Pledge Amount (TZS)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        name="default_pledge_amount"
                        value={priceSettings.default_pledge_amount}
                        onChange={handlePriceChange}
                        placeholder="Default pledge amount"
                      />
                      <Form.Text className="text-muted">Default amount suggested when adding a pledge.</Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="settings-form-label">Double Minimum Rate (TZS)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        name="double_minimum_rate"
                        value={priceSettings.double_minimum_rate}
                        onChange={handlePriceChange}
                        placeholder="Minimum amount for double card"
                      />
                      <Form.Text className="text-muted">Minimum pledge amount for a double card.</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-center mt-4">
                  <Button type="submit" variant="primary" className="settings-btn-primary" disabled={saving}>
                    {saving ? <Spinner animation="border" size="sm" /> : <FaSave className="me-2" />}
                    {saving ? ' Saving...' : ' Save Price Settings'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        {/* Message Templates Tab */}
        <Tab eventKey="message-templates" title={<span><FaEnvelope className="me-2" /> Message Templates</span>}>
          <Card className="settings-card">
            <Card.Body>
              <h3 className="settings-title">Message Templates</h3>
              <p className="text-muted mb-4">Configure all your notification and invitation messages</p>
              <Form onSubmit={handleSaveMessageTemplates}>
                <Row>
                  <Col md={6}>
                    {/* Card Invitation Intro */}
                    <div className="template-card mb-4">
                      <div className="template-card-header">
                        <div className="template-icon bg-primary">
                          <FaEnvelope size={20} />
                        </div>
                        <div className="template-title">
                          <h5 className="mb-0">Card Invitation Intro</h5>
                          <small className="text-muted">Wito wa kualika unaoonekana juu kwenye kadi ya mwaliko</small>
                        </div>
                      </div>
                      <div className="template-card-body">
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="invitation_intro"
                          value={messageTemplates.invitation_intro}
                          onChange={handleMessageTemplateChange}
                          placeholder="Familia pamoja na kamati ya maandalizi..."
                          className="template-textarea"
                        />
                      </div>
                    </div>

                    {/* Event Information Message */}
                    <div className="template-card mb-4">
                      <div className="template-card-header">
                        <div className="template-icon bg-info">
                          <FaBell size={20} />
                        </div>
                        <div className="template-title">
                          <h5 className="mb-0">Event Information Message</h5>
                          <small className="text-muted">Sent to Notify Guests about your Events</small>
                        </div>
                      </div>
                      <div className="template-card-body">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="event_information_message"
                          value={messageTemplates.event_information_message}
                          onChange={handleMessageTemplateChange}
                          placeholder="Habari #jina, Tuna furaha kukuarifu #shughuli ya #title..."
                          className="template-textarea"
                        />

                      </div>
                    </div>

                    {/* Pledge Acknowledgement Message */}
                    <div className="template-card mb-4">
                      <div className="template-card-header">
                        <div className="template-icon bg-success">
                          <FaHandHoldingUsd size={20} />
                        </div>
                        <div className="template-title">
                          <h5 className="mb-0">Pledge Acknowledgement</h5>
                          <small className="text-muted">Thank guests for their Contributions</small>
                        </div>
                      </div>
                      <div className="template-card-body">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="pledge_acknowledgement_message"
                          value={messageTemplates.pledge_acknowledgement_message}
                          onChange={handleMessageTemplateChange}
                          placeholder="Hello #jina. Asante kwa ahadi yako..."
                          className="template-textarea"
                        />

                      </div>
                    </div>

                    {/* Cash Acknowledgement Message */}
                    <div className="template-card mb-4">
                      <div className="template-card-header">
                        <div className="template-icon bg-success">
                          <FaCoins size={20} />
                        </div>
                        <div className="template-title">
                          <h5 className="mb-0">Cash Acknowledgement</h5>
                          <small className="text-muted">Thank guests for reducing | Paying their Pledges</small>
                        </div>
                      </div>
                      <div className="template-card-body">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="cash_acknowledgement_message"
                          value={messageTemplates.cash_acknowledgement_message}
                          onChange={handleMessageTemplateChange}
                          placeholder="Ahsante sana #jina, Mchango wako umepokelewa..."
                          className="template-textarea"
                        />

                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    {/* Card Invitation Info */}
                    <div className="template-card mb-4">
                      <div className="template-card-header">
                        <div className="template-icon bg-primary">
                          <FaEnvelope size={20} />
                        </div>
                        <div className="template-title">
                          <h5 className="mb-0">Card Invitation Info</h5>
                          <small className="text-muted">Taarifa za mwaliko zinazoonekana kwenye kadi ya mwaliko</small>
                        </div>
                      </div>
                      <div className="template-card-body">
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="invitation_info"
                          value={messageTemplates.invitation_info}
                          onChange={handleMessageTemplateChange}
                          placeholder="kwenye #shughuli ya #title Itakayofanyika..."
                          className="template-textarea"
                        />
                      </div>
                    </div>

                    {/* Pledge Reminder Message */}
                    <div className="template-card mb-4">
                      <div className="template-card-header">
                        <div className="template-icon bg-warning">
                          <FaBell size={20} />
                        </div>
                        <div className="template-title">
                          <h5 className="mb-0">Pledge Reminder Message</h5>
                          <small className="text-muted">Sent a reminder message to reminds your guests their pledges</small>
                        </div>
                      </div>
                      <div className="template-card-body">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="pledge_reminder_message"
                          value={messageTemplates.pledge_reminder_message}
                          onChange={handleMessageTemplateChange}
                          placeholder="Mpendwa #jina, kwa upendo mkubwa tunakukumbusha..."
                          className="template-textarea"
                        />

                      </div>
                    </div>

                    {/* Event Invitation Message */}
                    <div className="template-card mb-4">
                      <div className="template-card-header">
                        <div className="template-icon bg-primary">
                          <FaEnvelope size={20} />
                        </div>
                        <div className="template-title">
                          <h5 className="mb-0">Event Invitation Message</h5>
                          <small className="text-muted">Formal invitation sent to guests</small>
                        </div>
                      </div>
                      <div className="template-card-body">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="event_invitation_message"
                          value={messageTemplates.event_invitation_message}
                          onChange={handleMessageTemplateChange}
                          placeholder="Karibu #jina, kwenye #shughuli ya #title..."
                          className="template-textarea"
                        />

                      </div>
                    </div>

                    {/* Event Thank You Message */}
                    <div className="template-card mb-4">
                      <div className="template-card-header">
                        <div className="template-icon bg-secondary">
                          <FaHeart size={20} />
                        </div>
                        <div className="template-title">
                          <h5 className="mb-0">Event Thank You Message</h5>
                          <small className="text-muted">Post-event gratitude message</small>
                        </div>
                      </div>
                      <div className="template-card-body">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="event_thanknote_message"
                          value={messageTemplates.event_thanknote_message}
                          onChange={handleMessageTemplateChange}
                          placeholder="Asante kwa kushiriki nasi katika sherehe yetu..."
                          className="template-textarea"
                        />

                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="template-keymap mt-4 p-4 bg-light rounded border">
                  <h6 className="mb-3 text-primary d-flex align-items-center">
                    <FaInfoCircle className="me-2" /> Placeholder variables (Vishika nafasi)
                  </h6>
                  <p className="text-muted small mb-3">
                    Placeholders (Vishika nafasi) hivi hutumika kubadilisha taarifa, na kuweka taarifa halisi wakati wa kutuma
                  </p>
                  <div className="row g-3 mt-1">
                    <div className="col-12 col-sm-6 col-xl-4 d-flex align-items-center">
                      <span className="variable-badge me-2" style={{ minWidth: '110px', textAlign: 'center' }}>#jina</span>
                      <span className="text-muted small">Jina la mgeni</span>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4 d-flex align-items-center">
                      <span className="variable-badge me-2" style={{ minWidth: '110px', textAlign: 'center' }}>#title</span>
                      <span className="text-muted small">Jina la shughuli</span>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4 d-flex align-items-center">
                      <span className="variable-badge me-2" style={{ minWidth: '110px', textAlign: 'center' }}>#tarehe</span>
                      <span className="text-muted small">Tarehe ya shughuli</span>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4 d-flex align-items-center">
                      <span className="variable-badge me-2" style={{ minWidth: '110px', textAlign: 'center' }}>#siku</span>
                      <span className="text-muted small">Siku zilizobaki</span>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4 d-flex align-items-center">
                      <span className="variable-badge me-2" style={{ minWidth: '110px', textAlign: 'center' }}>#simu</span>
                      <span className="text-muted small">Namba ya simu ya Admin</span>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4 d-flex align-items-center">
                      <span className="variable-badge me-2" style={{ minWidth: '110px', textAlign: 'center' }}>#email</span>
                      <span className="text-muted small">Barua pepe (Email)</span>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4 d-flex align-items-center">
                      <span className="variable-badge me-2" style={{ minWidth: '110px', textAlign: 'center' }}>#tovuti</span>
                      <span className="text-muted small">Tovuti ya shughuli</span>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4 d-flex align-items-center">
                      <span className="variable-badge me-2" style={{ minWidth: '110px', textAlign: 'center' }}>#kadi</span>
                      <span className="text-muted small">Namba ya kadi</span>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4 d-flex align-items-center">
                      <span className="variable-badge me-2" style={{ minWidth: '110px', textAlign: 'center' }}>#mahali</span>
                      <span className="text-muted small">Mahali pa shughuli</span>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4 d-flex align-items-center">
                      <span className="variable-badge me-2" style={{ minWidth: '110px', textAlign: 'center' }}>#muda</span>
                      <span className="text-muted small">Muda wa shughuli</span>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-4 d-flex align-items-center">
                      <span className="variable-badge me-2" style={{ minWidth: '110px', textAlign: 'center' }}>#saliolaahadi</span>
                      <span className="text-muted small">Salio la ahadi</span>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-center mt-4">
                  <Button type="submit" variant="primary" className="settings-btn-primary" disabled={saving}>
                    {saving ? <Spinner animation="border" size="sm" /> : <FaSave className="me-2" />}
                    {saving ? ' Saving...' : ' Save All Message Templates'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>



        {/* Notification Settings Tab - Only enable/disable switches */}
        <Tab eventKey="notification" title={<span><FaBell className="me-2" /> Notification Settings</span>}>
          <Card className="settings-card">
            <Card.Body>
              <h3 className="settings-title">Notification Settings</h3>
              <p className="text-muted mb-4">Enable or disable automatic notifications</p>

              <Form onSubmit={handleSaveNotifications}>
                <div className="border rounded p-4 bg-light">
                  <Form.Group className="mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <Form.Label className="fw-bold mb-0">Auto Send Pledges</Form.Label>
                        <Form.Text className="text-muted d-block">
                          Automatically send acknowledgement when someone makes a pledge
                        </Form.Text>
                      </div>
                      <Form.Check
                        type="switch"
                        id="auto_send_pledges"
                        name="auto_send_pledges"
                        checked={notificationSettings.auto_send_pledges}
                        onChange={handleNotificationChange}
                        className="notification-switch-lg"
                      />
                    </div>
                  </Form.Group>

                  <Form.Group>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <Form.Label className="fw-bold mb-0">Auto Send Funds</Form.Label>
                        <Form.Text className="text-muted d-block">
                          Automatically send acknowledgement when cash contribution is received
                        </Form.Text>
                      </div>
                      <Form.Check
                        type="switch"
                        id="auto_send_funds"
                        name="auto_send_funds"
                        checked={notificationSettings.auto_send_funds}
                        onChange={handleNotificationChange}
                        className="notification-switch-lg"
                      />
                    </div>
                  </Form.Group>
                </div>

                <div className="d-flex justify-content-center mt-4">
                  <Button type="submit" variant="primary" className="settings-btn-primary" disabled={saving}>
                    {saving ? <Spinner animation="border" size="sm" /> : <FaSave className="me-2" />}
                    {saving ? ' Saving...' : ' Save Notification Settings'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default EventSettings;